/**
 * Field-level validation primitives — declarative `checks` array on
 * inputs that the renderer runs at the user's chosen trigger and
 * surfaces as inline errors.
 *
 * Mirrors `vercel-labs/json-render`'s ValidationConfig:
 *
 *   {
 *     "type": "TextInput",
 *     "stateKey": "/form/email",
 *     "checks": [
 *       { "type": "required", "message": "Email required" },
 *       { "type": "email",    "message": "Must be a valid email" }
 *     ],
 *     "validateOn": "blur"
 *   }
 *
 * Built-in checks: required, minLength, maxLength, min, max, pattern,
 * email, url, equals, oneOf. Host apps can register custom checks via
 * `registerValidationCheck`.
 */

export type ValidateTrigger = "change" | "blur" | "submit"

export interface ValidationCheck {
  /** Check type — built-in (e.g. "required") or registered. */
  type: string
  /** Error message shown when the check fails. */
  message: string
  /** Per-check arguments (e.g. {min: 5} for minLength). */
  args?: Record<string, unknown>
}

export interface ValidationConfig {
  checks?: ValidationCheck[]
  validateOn?: ValidateTrigger
}

/**
 * A validator function. Returns true when the value passes, false
 * when it fails. The caller is responsible for providing the message;
 * validators don't format errors themselves.
 */
export type Validator = (value: unknown, args: Record<string, unknown> | undefined) => boolean

const builtIns: Record<string, Validator> = {
  required: (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === "string") return value.trim().length > 0
    if (Array.isArray(value)) return value.length > 0
    return true
  },
  minLength: (value, args) => {
    const min = Number(args?.min ?? 0)
    if (typeof value !== "string") return true // not applicable
    return value.length >= min
  },
  maxLength: (value, args) => {
    const max = Number(args?.max ?? Number.POSITIVE_INFINITY)
    if (typeof value !== "string") return true
    return value.length <= max
  },
  min: (value, args) => {
    const min = Number(args?.min ?? Number.NEGATIVE_INFINITY)
    const n = Number(value)
    if (Number.isNaN(n)) return true // a non-number passes the numeric bound
    return n >= min
  },
  max: (value, args) => {
    const max = Number(args?.max ?? Number.POSITIVE_INFINITY)
    const n = Number(value)
    if (Number.isNaN(n)) return true
    return n <= max
  },
  pattern: (value, args) => {
    const pattern = typeof args?.pattern === "string" ? args.pattern : null
    if (!pattern) return true
    if (typeof value !== "string") return true
    try {
      // The pattern string isn't user-controlled at runtime — it's part
      // of the AI-generated spec — but we still defend against an
      // invalid regex by returning true (skip) rather than throwing
      // and breaking the input.
      const flags = typeof args?.flags === "string" ? args.flags : ""
      return new RegExp(pattern, flags).test(value)
    } catch {
      return true
    }
  },
  email: (value) => {
    if (typeof value !== "string" || value === "") return true
    // Pragmatic regex — the W3C HTML5 email rule plus a length cap.
    // Not RFC 5322 perfect but good enough for "looks like a real
    // email" form-level guard rails.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254
  },
  url: (value) => {
    if (typeof value !== "string" || value === "") return true
    try {
      const u = new URL(value)
      return u.protocol === "http:" || u.protocol === "https:"
    } catch {
      return false
    }
  },
  equals: (value, args) => value === args?.expected,
  oneOf: (value, args) => {
    const list = Array.isArray(args?.values) ? (args.values as unknown[]) : []
    return list.includes(value)
  },
}

const customChecks: Record<string, Validator> = {}

/**
 * Register a host-defined check. Idempotent overwrites for the same
 * name are allowed so a hot-reload can re-register without throwing.
 */
export function registerValidationCheck(type: string, validator: Validator): void {
  if (type in builtIns) {
    throw new Error(`Cannot register check "${type}": collides with a built-in`)
  }
  customChecks[type] = validator
}

/** Test-only: drop all registered custom checks. */

export function _resetCustomChecks(): void {
  for (const k of Object.keys(customChecks)) delete customChecks[k]
}

/** Look up a validator by check type. Returns undefined if unknown. */
export function getValidator(type: string): Validator | undefined {
  return builtIns[type] ?? customChecks[type]
}

/**
 * Run every check against a value. Returns the messages of failed
 * checks in the order they appear in the config. Empty array means
 * the value passed.
 *
 * Unknown check types are silently treated as passing (the spec
 * validator §2.5 surfaces the misconfiguration; here we don't want
 * to render an error message that has no associated check name).
 */
export function evaluateChecks(value: unknown, checks: ValidationCheck[] | undefined): string[] {
  if (!checks || checks.length === 0) return []
  const errors: string[] = []
  for (const check of checks) {
    const validator = getValidator(check.type)
    if (!validator) continue
    if (!validator(value, check.args)) {
      errors.push(check.message)
    }
  }
  return errors
}
